import { privateApi } from '@/config/axios.config';

export interface InvoiceCreateDTO {
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    customerCompany?: string;
    customerRNC?: string;
    customerAddress?: string;
    type: 'sale' | 'rental' | 'service' | 'commission' | 'other';
    description?: string;
    notes?: string;
    issueDate: string;
    dueDate?: string;
    items?: Array<{
        description: string;
        quantity: number;
        unitPrice: number;
        taxRate?: number;
    }>;
    currency: string;
    taxRate?: number;
    discountRate?: number;
}

export interface Invoice {
    id: string;
    invoiceNumber: string;
    tenantId: string;
    leadId?: string;
    propertyId?: string;
    createdBy: string;
    approvedBy?: string;
    customerName: string;
    customerEmail: string;
    customerPhone?: string;
    customerCompany?: string;
    customerRNC?: string;
    customerAddress?: string;
    type: 'sale' | 'rental' | 'service' | 'commission' | 'other';
    status: string;
    description?: string;
    notes?: string;
    issueDate: string;
    dueDate?: string;
    paidAt?: string;
    subtotal: number;
    taxAmount: number;
    discountAmount: number;
    total: number;
    currency: string;
    pdfUrl?: string;
    createdAt: string;
    updatedAt: string;
}

export interface InvoiceFilters {
    search?: string;
    status?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
    customerEmail?: string;
    propertyId?: string;
    leadId?: string;
    minAmount?: number;
    maxAmount?: number;
    page?: number;
    limit?: number;
}

export interface InvoiceStats {
    totalInvoices: number;
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    overdueAmount: number;
    current: { count: number; amount: number };
    days1to30: { count: number; amount: number };
    days31to60: { count: number; amount: number };
    days61to90: { count: number; amount: number };
    over90: { count: number; amount: number };
}

export interface InvoiceListResponse {
    data: Invoice[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

const BASE_PATH = '/financial/invoices';

class InvoiceApiService {
    // Obtener todas las facturas con filtros
    async getInvoices(filters: InvoiceFilters = {}): Promise<InvoiceListResponse> {
        const response = await privateApi.get(BASE_PATH, { params: filters });
        return response.data;
    }

    // Obtener factura por ID
    async getInvoice(id: string): Promise<Invoice> {
        const response = await privateApi.get(`${BASE_PATH}/${id}`);
        return response.data;
    }

    // Crear nueva factura
    async createInvoice(data: InvoiceCreateDTO): Promise<Invoice> {
        const response = await privateApi.post(BASE_PATH, data);
        return response.data;
    }

    // Obtener estadísticas
    async getStats(): Promise<InvoiceStats> {
        const response = await privateApi.get(`${BASE_PATH}/stats`);
        return response.data;
    }

    // Descargar factura
    async downloadInvoice(id: string): Promise<Blob> {
        const response = await privateApi.get(`${BASE_PATH}/${id}/download`, {
            responseType: 'blob',
        });
        return response.data;
    }

    // Obtener facturas vencidas
    async getOverdueInvoices(filters: InvoiceFilters = {}): Promise<InvoiceListResponse> {
        const response = await privateApi.get(`${BASE_PATH}/overdue`, { params: filters });
        return response.data;
    }
}

export const invoiceApiService = new InvoiceApiService();
export default invoiceApiService;
